/**
 * LanguageSelector — medium-tier component.
 *
 * Allows a logged-in user to change their UI language.
 * Calls PATCH /auth/me to persist the choice, updates authStore,
 * and immediately changes i18next language.
 *
 * Uses optimistic UI: switches language immediately, reverts on API error.
 */

import { useState } from 'react'
import { toast } from 'sonner'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { updateLocale } from '@/services/api'
import type { Locale } from '@/types/auth'

export function LanguageSelector() {
    const { t } = useTranslation()
    const user = useAuthStore((s) => s.user)
    const setUser = useAuthStore((s) => s.setUser)
    const [isLoading, setIsLoading] = useState(false)

    const currentLocale: Locale = user !== null && user !== undefined ? user.locale : 'pl'

    const handleChange = async (newLocale: Locale) => {
        if (newLocale === currentLocale || isLoading) return

        // Capture full previous user snapshot for optimistic revert
        const previousUser = useAuthStore.getState().user
        if (!previousUser) return

        setIsLoading(true)

        // Optimistic update
        setUser({ ...previousUser, locale: newLocale })
        i18next.changeLanguage(newLocale)

        try {
            const updatedUser = await updateLocale(newLocale)
            setUser(updatedUser)
        } catch {
            // Revert on failure
            setUser(previousUser)
            i18next.changeLanguage(previousUser.locale)
            toast.error(t('profile.languageChangeError'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{t('profile.language')}</span>
            <div className="flex rounded-md border border-border overflow-hidden">
                <button
                    onClick={() => handleChange('pl')}
                    disabled={isLoading}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        currentLocale === 'pl'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                >
                    PL
                </button>
                <button
                    onClick={() => handleChange('en')}
                    disabled={isLoading}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-l border-border ${
                        currentLocale === 'en'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
                    }`}
                >
                    EN
                </button>
            </div>
        </div>
    )
}
